/* eslint-disable react/no-unescaped-entities */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Plus, Star, Phone, Trash2, Pencil, Search, X, Loader2, UserPlus, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import { walletApi } from "@/lib/wallet";
import SEO from "@/components/site/SEO";
import CustomerBottomNav from "@/components/customer/CustomerBottomNav";

function ContactForm({ initial, onCancel, onSave, saving }) {
  const [name, setName] = useState(initial?.name || "");
  const [phone, setPhone] = useState(initial?.phone_e164 || "+");
  const [country, setCountry] = useState(initial?.country || "");

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSave({ name, phone, country: country || null }); }}
      className="space-y-3"
      data-testid="contact-form"
    >
      <div>
        <label className="block text-xs font-semibold text-brand-muted mb-1">Name</label>
        <input
          required maxLength={80}
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="contact-name-input"
          className="w-full rounded-lg border border-brand-line bg-white px-3 py-2.5 text-sm focus:border-brand-green focus:outline-none"
          placeholder="e.g. Mum"
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-brand-muted mb-1">Phone (international)</label>
        <input
          required
          value={phone}
          onChange={(e) => setPhone(e.target.value.replace(/[^\d+]/g, ""))}
          data-testid="contact-phone-input"
          className="w-full rounded-lg border border-brand-line bg-white px-3 py-2.5 text-sm font-mono focus:border-brand-green focus:outline-none"
          placeholder="+2348012345678"
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-brand-muted mb-1">Country (optional)</label>
        <input
          maxLength={2}
          value={country}
          onChange={(e) => setCountry(e.target.value.toUpperCase().replace(/[^A-Z]/g, ""))}
          data-testid="contact-country-input"
          className="w-full rounded-lg border border-brand-line bg-white px-3 py-2.5 text-sm uppercase focus:border-brand-green focus:outline-none"
          placeholder="NG"
        />
      </div>
      <div className="flex items-center justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} data-testid="contact-form-cancel"
          className="rounded-full border border-brand-line bg-white px-4 py-2 text-xs font-semibold text-brand-ink hover:bg-brand-bgSoft">
          Cancel
        </button>
        <button type="submit" disabled={saving} data-testid="contact-form-save"
          className="inline-flex items-center gap-2 rounded-full bg-brand-green hover:bg-brand-greenHover disabled:opacity-60 text-white px-4 py-2 text-xs font-bold">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          {initial ? "Save changes" : "Add contact"}
        </button>
      </div>
    </form>
  );
}

export default function CustomerContacts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showOnlyFavs, setShowOnlyFavs] = useState(false);
  const [editing, setEditing] = useState(null);   // contact obj
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (showOnlyFavs) params.set("favorite", "true");
      const { data } = await walletApi.get(`/customer/contacts?${params.toString()}`);
      setItems(data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load contacts");
    } finally { setLoading(false); }
  }, [q, showOnlyFavs]);

  useEffect(() => {
    const t = setTimeout(refresh, 250); // debounce search
    return () => clearTimeout(t);
  }, [refresh]);

  async function handleCreate(form) {
    setSaving(true);
    try {
      await walletApi.post("/customer/contacts", form);
      toast.success("Contact saved");
      setCreating(false);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save contact");
    } finally { setSaving(false); }
  }

  async function handleUpdate(form) {
    setSaving(true);
    try {
      await walletApi.patch(`/customer/contacts/${editing.id}`, form);
      toast.success("Contact updated");
      setEditing(null);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update contact");
    } finally { setSaving(false); }
  }

  async function toggleFav(c) {
    // optimistic
    setItems((prev) => prev.map((x) => x.id === c.id ? { ...x, favorite: !x.favorite } : x));
    try {
      await walletApi.post(`/customer/contacts/${c.id}/favorite`);
    } catch (err) {
      toast.error("Could not update favorite");
      refresh();
    }
  }

  async function handleDelete(c) {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    try {
      await walletApi.delete(`/customer/contacts/${c.id}`);
      setItems((prev) => prev.filter((x) => x.id !== c.id));
      toast.success("Contact deleted");
    } catch (err) {
      toast.error("Could not delete contact");
    }
  }

  function callContact(c) {
    // Stash for the dialer to pick up + navigate.
    try {
      sessionStorage.setItem("od_dial_prefill", JSON.stringify({
        number: c.phone_e164, name: c.name, ts: Date.now(),
      }));
    } catch (err) {
      // Non-fatal — Safari private-mode / quota-exceeded paths.
      console.warn("dial prefill stash failed", err?.message || err);
    }
    nav("/app/dial");
  }

  const favoritesFirst = [...items].sort((a, b) => {
    if (a.favorite !== b.favorite) return a.favorite ? -1 : 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <div className="min-h-screen bg-brand-bgSoft text-brand-ink pb-24 md:pb-10" data-testid="customer-contacts">
      <SEO title="Contacts" path="/app/contacts" />
      <header className="bg-white border-b border-brand-line sticky top-0 z-30">
        <div className="container-page flex items-center justify-between h-16">
          <Link to="/app" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-ink/80 hover:text-brand-ink" data-testid="contacts-back">
            <ArrowLeft className="h-4 w-4" /> Dashboard
          </Link>
          <button type="button" onClick={() => { setCreating(true); setEditing(null); }}
            data-testid="contacts-new-btn"
            className="inline-flex items-center gap-2 rounded-full bg-brand-green hover:bg-brand-greenHover text-white px-4 py-2 text-xs font-bold">
            <UserPlus className="h-3.5 w-3.5" /> New
          </button>
        </div>
      </header>

      <div className="container-page py-6 max-w-2xl">
        <div className="flex items-center gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or number"
              data-testid="contacts-search"
              className="w-full rounded-full bg-white border border-brand-line pl-9 pr-9 py-2.5 text-sm focus:border-brand-green focus:outline-none"
            />
            {q && (
              <button type="button" onClick={() => setQ("")} data-testid="contacts-search-clear"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 hover:bg-brand-bgSoft">
                <X className="h-3.5 w-3.5 text-brand-muted" />
              </button>
            )}
          </div>
          <button type="button" onClick={() => setShowOnlyFavs((v) => !v)}
            data-testid="contacts-favs-toggle"
            className={`inline-flex items-center gap-1 rounded-full border px-3 py-2 text-xs font-semibold ${
              showOnlyFavs ? "bg-amber-100 border-amber-300 text-amber-800"
                           : "bg-white border-brand-line text-brand-muted hover:bg-brand-bgSoft"}`}>
            <Star className={`h-3.5 w-3.5 ${showOnlyFavs ? "fill-amber-500 stroke-amber-500" : ""}`} />
            Favorites
          </button>
        </div>

        {(creating || editing) && (
          <div className="rounded-2xl border border-brand-line bg-white p-5 mb-5" data-testid="contact-form-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-black text-base">{editing ? "Edit contact" : "Add a contact"}</h3>
            </div>
            <ContactForm
              initial={editing}
              saving={saving}
              onCancel={() => { setCreating(false); setEditing(null); }}
              onSave={editing ? handleUpdate : handleCreate}
            />
          </div>
        )}

        {loading ? (
          <div className="rounded-2xl border border-brand-line bg-white p-8 text-center text-sm text-brand-muted" data-testid="contacts-loading">
            <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading contacts…
          </div>
        ) : favoritesFirst.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-brand-line bg-white p-10 text-center" data-testid="contacts-empty">
            <p className="font-display font-black text-lg">No contacts yet</p>
            <p className="mt-1 text-sm text-brand-muted">
              {q || showOnlyFavs ? "No matches. Try a different search." : "Save your first phone number to call it with a tap."}
            </p>
            {!q && !showOnlyFavs && (
              <button type="button" onClick={() => setCreating(true)}
                data-testid="contacts-empty-cta"
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-green hover:bg-brand-greenHover text-white px-4 py-2 text-xs font-bold">
                <Plus className="h-3.5 w-3.5" /> Add first contact
              </button>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-brand-line rounded-2xl border border-brand-line bg-white overflow-hidden" data-testid="contacts-list">
            {favoritesFirst.map((c) => (
              <li key={c.id} className="flex items-center gap-3 p-4" data-testid={`contact-row-${c.id}`}>
                <button type="button" onClick={() => toggleFav(c)} data-testid={`contact-fav-${c.id}`}
                  className="shrink-0 rounded-full p-1.5 hover:bg-brand-bgSoft"
                  aria-label={c.favorite ? "Unfavorite" : "Favorite"}>
                  <Star className={`h-4 w-4 ${c.favorite ? "fill-amber-500 stroke-amber-500" : "text-brand-muted"}`} />
                </button>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-brand-ink truncate" data-testid={`contact-name-${c.id}`}>{c.name}</p>
                  <p className="text-xs text-brand-muted font-mono" data-testid={`contact-phone-${c.id}`}>
                    {c.phone_e164}{c.country ? ` · ${c.country}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button type="button" onClick={() => callContact(c)} data-testid={`contact-call-${c.id}`}
                    className="inline-flex items-center justify-center rounded-full bg-brand-green hover:bg-brand-greenHover text-white h-9 w-9"
                    aria-label="Call">
                    <Phone className="h-4 w-4" />
                  </button>
                  <Link to={`/app/messages/${encodeURIComponent(c.phone_e164)}`}
                    data-testid={`contact-sms-${c.id}`}
                    className="inline-flex items-center justify-center rounded-full bg-sky-500 hover:bg-sky-600 text-white h-9 w-9"
                    aria-label="Message">
                    <MessageSquare className="h-4 w-4" />
                  </Link>
                  <button type="button" onClick={() => { setEditing(c); setCreating(false); }} data-testid={`contact-edit-${c.id}`}
                    className="inline-flex items-center justify-center rounded-full border border-brand-line hover:bg-brand-bgSoft h-9 w-9"
                    aria-label="Edit">
                    <Pencil className="h-4 w-4 text-brand-ink" />
                  </button>
                  <button type="button" onClick={() => handleDelete(c)} data-testid={`contact-delete-${c.id}`}
                    className="inline-flex items-center justify-center rounded-full border border-brand-line hover:bg-rose-50 hover:border-rose-200 h-9 w-9"
                    aria-label="Delete">
                    <Trash2 className="h-4 w-4 text-rose-600" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <CustomerBottomNav />
    </div>
  );
}
